import React from "react";
import { Box, VStack } from "@chakra-ui/react";

import { colors } from "../../../styles";
import { call, customEvent } from "../../../utils";
import { ColorIcon, HStack } from "../../common";
import { GOI_CALL_NUMBER } from "../../../constants/crematoriumInquiry";
import { EventDomainType, EventPathType } from "../../../types";

type InquiryTrigger =
  | "ask-book"
  | "ask-reloc"
  | "ask-visit"
  | "ask-etc"
  | "ask-binso"
  | "ask-cost";

export interface InquiryOption {
  label: string;
  trigger: InquiryTrigger;
  /** goi: 고이 대표번호로, facility: 화장장 번호로 (번호가 없으면 고이로 폴백) */
  target: "goi" | "facility";
  /** true 면 발신 시 새벽 2~7시 고객센터 안내(차단)를 건너뛴다. 임종 직후처럼 새벽에도 연결돼야 하는 문의 */
  urgent?: boolean;
}

/**
 * 트리거를 짧게 쓰는 이유.
 * 이 레포의 이벤트명은 `click-{domain}-{path}-{trigger}` 라 domain 이 한 겹 더 붙는다.
 * GA4 40자 기준으로 예산이 가장 빠듯한 화면이 gyeongsang/nat_fr-crema 의 10자라
 * booking, relocation 을 book, reloc 으로 줄였다. 나머지 셋은 platform 과 이름이 같다.
 */
export const CREMATORIUM_INQUIRY_OPTIONS: InquiryOption[] = [
  { label: "화장 예약 및 비용 문의", trigger: "ask-book", target: "goi" },
  { label: "개장·이장 관련 문의", trigger: "ask-reloc", target: "facility" },
  { label: "고인 확인 및 방문 문의", trigger: "ask-visit", target: "facility" },
  { label: "기타 문의", trigger: "ask-etc", target: "goi" },
];

/** 장례식장: 조문 문의만 장례식장 직통, 나머지는 고이 */
export const PARLOR_INQUIRY_OPTIONS: InquiryOption[] = [
  {
    label: "빈소 예약 및 고인 이송 문의",
    trigger: "ask-binso",
    target: "goi",
    urgent: true,
  },
  { label: "장례 비용 문의", trigger: "ask-cost", target: "goi" },
  {
    label: "조문 문의 (위치, 주차, 화환 등)",
    trigger: "ask-visit",
    target: "facility",
  },
  { label: "기타 문의", trigger: "ask-etc", target: "goi" },
];

export interface CrematoriumInquiryModalProps {
  /** 버튼 구성. CREMATORIUM_INQUIRY_OPTIONS / PARLOR_INQUIRY_OPTIONS */
  options: InquiryOption[];
  /** 이벤트 domain. 포털별로 다르다 */
  eventDomain: keyof typeof EventDomainType;
  /** 이벤트 path. 화면별로 다르다 (CREMATORIUM_INQUIRY_EVENT_PATH 참고) */
  eventPath: EventPathType;
  /** 화장장 대표번호. 데이터에 없으면 null 이고 고이로 폴백한다 */
  facilityPhone: string | null;
  /** 선택 후 닫기. 이벤트는 각 버튼이 따로 쏘므로 여기서는 닫기만 한다 */
  onClose: () => void;
}

/** 문의 모달 헤더: 제목 + 닫기. onClose 가 ask-close 이벤트까지 책임진다. */
export function CrematoriumInquiryModalHeader({
  onClose,
}: {
  onClose: () => void;
}) {
  return (
    <HStack
      width="100%"
      justifyContent="space-between"
      alignItems="flex-start"
      padding="24px 24px 0px"
      style={{ wordBreak: "keep-all" }}
    >
      <Box className="subtitle_21_b" color={colors.gray900}>
        어떤 문의가 필요하신가요?
      </Box>
      <Box
        as="button"
        aria-label="닫기"
        background="none"
        border="none"
        flexShrink={0}
        cursor="pointer"
        lineHeight={0}
        onClick={onClose}
      >
        <ColorIcon name="close" width={24} height={24} color={colors.gray500} />
      </Box>
    </HStack>
  );
}

/**
 * 화장장 시설 상세의 전화 CTA 를 눌렀을 때 뜨는 문의 유형 선택 모달.
 *
 * 화장 예약·비용과 기타는 고이 상담으로, 개장·이장과 고인 확인·방문은
 * 해당 화장장으로 건다. 화장장 번호가 없는 시설은 고이로 폴백한다.
 *
 * 모달을 여는 버튼의 이벤트는 각 화면이 기존 이름 그대로 쏘고,
 * 여기서는 모달 안의 5개(선택 4 + 닫기)만 쏜다.
 */
export function CrematoriumInquiryModal({
  eventDomain,
  options,
  eventPath,
  facilityPhone,
  onClose,
}: CrematoriumInquiryModalProps) {
  const handleSelect = (option: InquiryOption) => {
    customEvent({
      domain: eventDomain,
      path: eventPath,
      trigger_and_target: option.trigger,
    });

    onClose();
    call(
      option.target === "facility" && facilityPhone
        ? facilityPhone
        : GOI_CALL_NUMBER,
      Boolean(option.urgent)
    );
  };

  return (
    <VStack width="100%" gap="10px" padding="20px 24px 24px">
      {options.map((option) => (
        <Box
          key={option.trigger}
          as="button"
          width="100%"
          height="52px"
          background={colors.white}
          border={`1px solid ${colors.gray200}`}
          borderRadius="12px"
          className="subtitle_16_b"
          color={colors.gray900}
          cursor="pointer"
          onClick={() => handleSelect(option)}
        >
          {option.label}
        </Box>
      ))}
    </VStack>
  );
}
